<template>
    <div class="row">
        <div class="col-12 lg:col-8">
            <ul v-if="carts.length > 0" class="mb-6">
                <li v-for="(cart, index) in carts"
                    class="flex items-center gap-3 mb-4 border rounded-lg p-3 last:mb-0">
                    <img :src="cart.image" alt="products" class="w-28 rounded-lg flex-shrink-0" />

                     <div class="flex flex-col items-start justify-center gap-2 relative w-full overflow-hidden">
                        <h4 class="font-semibold capitalize flex items-center gap-2 whitespace-nowrap overflow-hidden text-ellipsis w-full pb-2 border-b">
                            <span>{{ cart.name }}</span>
                            <span v-if="cart.reserved_until"class="inline-flex items-center gap-1.5 px-2.5 py-0.5 mb-1 rounded-full text-xs font-medium" :class="isReservationActive(cart.reserved_until) ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-danger'">
                                {{ isReservationActive(cart.reserved_until) ? $t('label.reserved_for_you') + ' ' + timeLeft(cart.reserved_until) + ' min.' : $t('label.reservation_expired') }}
                            </span>
                        </h4>
                        <div class="sm:flex sm:items-center sm:justify-between w-full">
                            <div class="flex flex-col w-full gap-1">
                                <div class="flex items-center gap-1 w-20 p-1 rounded-full bg-[#F7F7FC]">
                                    <button @click.prevent="quantityDecrement(index, cart)" type="button"
                                        :class="cart.quantity === 1 ? 'cursor-not-allowed' : ''"
                                        class="lab-fill-circle-minus text-lg leading-none transition-all duration-300 hover:text-primary"></button>
                                    <input v-on:keypress="onlyNumber($event)" v-on:keyup="quantityUp(index, cart, $event)"
                                        type="number" v-model="cart.quantity"
                                        class="text-center w-full h-5 text-sm font-medium">
                                    <button
                                        :class="cart.quantity >= cart.stock ? 'cursor-not-allowed' : cart.quantity >= cart.maximum_purchase_quantity ? 'cursor-not-allowed' : ''"
                                        @click.prevent="quantityIncrement(index, cart)" type="button"
                                        class="lab-fill-circle-plus text-lg leading-none transition-all duration-300 hover:text-primary"></button>
                                </div>
                                <div v-if="cart.variation_id > 0" class="flex flex-wrap gap-1">
                                    <span class="text-xs capitalize inline-flex items-center">{{ cart.variation_names }}</span>
                                </div>
                                <div class="flex flex-wrap gap-3">
                                    <span class="font-semibold font-sans">{{ currencyFormat(cart.price,
                                    setting.site_digit_after_decimal_point,
                                    setting.site_default_currency_symbol, setting.site_currency_position) }}</span>
                                    <del v-if="cart.discount > 0" class="font-semibold font-sans text-[#FF6262]">
                                        {{ currencyFormat(cart.old_price, setting.site_digit_after_decimal_point,
                                            setting.site_default_currency_symbol, setting.site_currency_position) }}
                                    </del>
                                </div>
                            </div>
                            <div class="flex items-center gap-1">
                                <button @click.prevent="removeProduct(index)"
                                    class="pointer-events-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFF4F4] text-danger transition-all duration-300 hover:bg-[#E93C3C] hover:text-white">
                                    <i class="lab-line-trash text-sm"></i>
                                    <span class="text-xs font-medium capitalize hidden sm:block">{{ $t('button.remove')
                                        }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>

            <div class="text-left">
                <router-link :to="{name : 'frontend.checkout.checkout'}" class="max-lg:hidden field-button w-fit font-semibold tracking-wide normal-case">{{ $t('button.continue_to_shippment') }}</router-link>
            </div>
        </div>

        <div class="col-12 lg:col-4">
            <CouponComponent v-if="coupons.length > 0" />
            <SummeryComponent />

            <router-link :to="{ name : 'frontend.checkout.checkout' }" class="max-lg:block hidden field-button mt-6 font-semibold tracking-wide normal-case">{{ $t('button.continue_to_shippment') }}</router-link>
        </div>
    </div>
</template>

<script>
import appService from "../../../../services/appService";
import CouponComponent from "../CouponComponent.vue";
import SummeryComponent from "../SummeryComponent.vue";
import ShippingTypeEnum from "../../../../enums/modules/shippingTypeEnum";

export default {
    name: "CartListComponent",
    components: {SummeryComponent, CouponComponent},
    data() {
        return {
            now: new Date().getTime(),
            timerInterval: null
        }
    },
    mounted() {
        this.timerInterval = setInterval(() => {
            this.now = new Date().getTime();
        }, 1000);
    },
    beforeUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    computed: {
        coupons: function () {
            return this.$store.getters['frontendCoupon/lists'];
        },
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        carts: function () {
            return this.$store.getters['frontendCart/activeLists'];
        }
    },
    methods: {
        parseDate: function (dateStr) {
            if (!dateStr) return 0;
            if (typeof dateStr === 'string') {
                return new Date(dateStr.replace(' ', 'T')).getTime();
            }
            return new Date(dateStr).getTime();
        },
        isReservationActive: function (reservedUntil) {
            if (!reservedUntil) return false;
            return this.parseDate(reservedUntil) - this.now > 0;
        },
        timeLeft: function (reservedUntil) {
            if (!reservedUntil) return '';
            const diff = this.parseDate(reservedUntil) - this.now;
            if (diff <= 0) return '00:00';
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },
        onlyNumber: function (e) {
            return appService.onlyNumber(e);
        },
        currencyFormat(amount, decimal, currency, position) {
            return appService.currencyFormat(amount, decimal, currency, position);
        },
        isFreeShipping: function (cart) {
            return parseInt(cart?.shipping?.shipping_type) === ShippingTypeEnum.FREE;
        },
        quantityUp: function (id, product, e) {
            let quantity = e.target.value;

            if (quantity === 0) {
                quantity = 1;
            }
            if (quantity > product.stock) {
                quantity = product.stock
            }
            this.$store.dispatch('frontendCart/quantity', {id: id, status: quantity}).then().catch();
        },
        quantityIncrement: function (id, product) {
            let quantity = product.quantity;
            quantity++;
            if (quantity <= 0) {
                quantity = 1;
            }

            if (quantity > product.stock) {
                quantity--;
            }
            this.$store.dispatch('frontendCart/quantity', {id: id, status: quantity}).then().catch();
        },
        quantityDecrement: function (id, product) {
            let quantity = product.quantity;
            quantity--;
            if (quantity <= 0) {
                quantity = 1;
            }
            this.$store.dispatch('frontendCart/quantity', {id: id, status: quantity}).then().catch();
        },
        removeProduct: function (id) {
            this.$store.dispatch('frontendCart/remove', {id: id}).then().catch();
        }
    }
}
</script>